import type { AgentRow } from '../Agents/api';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { mintOpId } from '../Agents/opId';

export type ComposerRoute = { kind: 'voyager'; text: string } | { kind: 'note'; text: string }
  | { kind: 'agent'; agent: AgentRow; text: string; direction: boolean };

/** A leading address wins over the selected perspective; ambiguity never steers the wrong seat. */
export function composerRoute(text: string, agents: AgentRow[]): ComposerRoute {
  const clean = text.trim();
  if (!clean.startsWith('@')) return { kind: 'voyager', text: clean };
  if (/^@group(?:\b|:)/i.test(clean)) return { kind: 'note', text: 'Groups arrive with #307.' };
  const matches = agents.filter((a) => a.state !== 'retired').flatMap((agent) =>
    [agent.name, agent.seat?.title].filter((name): name is string => !!name).flatMap((name) => {
      const prefix = `@${name}`;
      return clean.slice(0, prefix.length).toLowerCase() === prefix.toLowerCase()
        && (clean.length === prefix.length || /[\s,:]/.test(clean[prefix.length])) ? [{ agent, length: prefix.length }] : [];
    })).sort((a, b) => b.length - a.length);
  if (!matches.length) return { kind: 'note', text: 'No agent matches that name or seat. Groups arrive with #307.' };
  const best = matches[0];
  if (matches.some((m) => m.length === best.length && m.agent.id !== best.agent.id))
    return { kind: 'note', text: 'More than one agent matches. Use its unique name or seat title.' };
  const message = clean.slice(best.length).replace(/^[\s,:]+/, '');
  if (!message) return { kind: 'note', text: 'Add a question or direction after the agent’s name.' };
  return { kind: 'agent', agent: best.agent, text: message,
    direction: !message.includes('?') && !/^(what|why|how|when|where|who|which|is|are|do|does|did|can|could|should)\b/i.test(message) };
}

export function sendToAgent(route: Extract<ComposerRoute, { kind: 'agent' }>) {
  const state = useVoyagerStore.getState();
  const threadId = state.openAgentChat(route.agent.id, route.agent.name);
  const op = mintOpId();
  void state.deliverChatTurn(threadId, route.agent.id, { id: op, op_id: op, role: 'user', text: route.text, ts: new Date().toISOString() })
    .catch(() => { /* The durable turn exposes its failure and retry in AgentChat. */ });
}
